// Catalog.ID Client — Trust Crypto Helpers
// ============================================================================
// Generates cryptographic commitments and signatures for trust expressions
// and claim endorsements. Uses ML-DSA-65 for signatures and SHA-256 for
// commitments, matching the server-side verification protocol.

import { signHybrid, wrapperToJson } from './signing';
import { sha256, toBase64, encodeUtf8, fromBase64 } from './aes';
import { keys } from '../state/keys';

/**
 * Generate a cryptographic commitment for a trust expression.
 * commitment = SHA-256(UTF8(sourceCid) || nonce)
 * nonce = random 32 bytes
 */
export async function generateTrustCommitment(sourceCid: string): Promise<{
    nonce: string;
    commitment: string;
}> {
    const nonceBytes = crypto.getRandomValues(new Uint8Array(32));
    const cidBytes = encodeUtf8(sourceCid);
    const combined = new Uint8Array(cidBytes.length + nonceBytes.length);
    combined.set(cidBytes, 0);
    combined.set(nonceBytes, cidBytes.length);
    const hash = await sha256(combined);
    return {
        nonce: toBase64(nonceBytes),
        commitment: toBase64(hash),
    };
}

/**
 * Sign a trust expression: sign("trust|{targetCid}|{score}").
 * Returns a JCS-canonical JSON SignatureWrapper string.
 * Throws if the signing key is not loaded.
 */
export function signTrustExpression(targetCid: string, score: number): string {
    const secretKey = getSigningKey();
    const message = encodeUtf8(`trust|${targetCid}|${score}`);
    return wrapperToJson(signHybrid(message, secretKey));
}

/**
 * Sign a claim endorsement: sign("endorse|{claimUuid}|{endorsement}").
 * Returns a JCS-canonical JSON SignatureWrapper string.
 * Throws if the signing key is not loaded.
 */
export function signEndorsement(claimUuid: string, endorsement: boolean): string {
    const secretKey = getSigningKey();
    const message = encodeUtf8(`endorse|${claimUuid}|${endorsement ? '1' : '0'}`);
    return wrapperToJson(signHybrid(message, secretKey));
}

function getSigningKey(): Uint8Array {
    const keyB64 = keys.getPrivateKey('main_signing_key');
    if (!keyB64) {
        throw new Error('Signing key not available — unlock your keys first');
    }
    return fromBase64(keyB64);
}
